import { useMemo, type FC } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, SpinAnimation } from "@twioku/common_components";
import { useRequireAgreedAnswerer } from "../features/answerer/hooks/useRequireAgreedAnswerer";
import { AnswererProfileImage } from "../components/AnswererProfileImage";
import { Path } from "../routes/PagePath";
import VectorIcon from "../assets/vector_icon.svg";
import { useUserQuestions } from "../features/question/hooks/useUserQuestions";
import { SendStatusType } from "../features/question/types/types";
import {
    UserQuestion,
    UserQuestionStatusSendOrderOneOf,
    UserQuestionStatusSendOrderOneOf1,
    UserQuestionStatusSendOrderOneOf2,
} from "../api/generated";
import { toFormatString } from "../libs/helper";
import ChevronsRight from "../assets/chevrons_right.svg";

type StatusByDisplayInfo = {
    status: SendStatusType;
    label: string;
    styled: string;
    displayDate: string;
    to?: string;
};

const getStatusByDisplayInfo = (
    userQuestion: UserQuestion,
    answererId: string
): StatusByDisplayInfo | undefined => {
    if (userQuestion.statusSendOrder.status === "NOT_SENT") {
        const baseDisplayInfo: Omit<StatusByDisplayInfo, "displayDate"> = {
            status: "NOT_SENT",
            label: "送信前",
            styled: "bg-gray-100 text-gray-500 border border-gray-200",
        };
        if (!userQuestion.statusSendOrder.scheduledSendingDateTime) {
            return {
                ...baseDisplayInfo,
                displayDate: "",
            };
        }

        const date = new Date(
            userQuestion.statusSendOrder.scheduledSendingDateTime
        );
        const formatDate = toFormatString(date, true, "/");
        return {
            status: "NOT_SENT",
            label: "送信前",
            styled: "bg-gray-100 text-gray-500 border border-gray-200",
            displayDate: `${formatDate} 送信予定`,
            to: `${Path.updateQuestion}/${answererId}/${userQuestion.id}`,
        };
    }

    if (userQuestion.statusSendOrder.status === "SENT") {
        const date = new Date(userQuestion.statusSendOrder.sentDateTime);
        const formatDate = toFormatString(date, true, "/");
        return {
            status: "SENT",
            label: "送信済",
            styled: "bg-gray-100 text-primary border border-gray-200",
            displayDate: `${formatDate} 送信済み`,
            to: `${Path.sentQuestionDetail}/${answererId}/${userQuestion.id}`,
        };
    }

    if (userQuestion.statusSendOrder.status === "RESPONDED") {
        const date = new Date(userQuestion.statusSendOrder.respondedDateTime);
        const formatDate = toFormatString(date, true, "/");
        return {
            status: "RESPONDED",
            label: "回答済",
            styled: "bg-primary text-white",
            displayDate: `${formatDate} 回答済み`,
            to: `/memory/${userQuestion.statusSendOrder.memoryId}`,
        };
    }

    return undefined;
};

const UserQuestionRowContent = ({
    displayDate,
    title,
    label,
    styled,
}: {
    displayDate: string;
    title: string;
    label: string;
    styled: string;
}) => (
    <>
        <div className="ml-2 mt-1 text-sm text-gray-500">{displayDate}</div>
        <div className="flex w-full justify-between border-b-2 pb-3">
            <span className="ml-3 flex min-w-0 flex-1 items-center">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {title}
                </span>
            </span>
            <div className="mr-3 flex items-center justify-between">
                <span
                    className={`rounded-md px-2 py-1 text-sm font-medium ${styled}`}
                >
                    {label}
                </span>
                <img className="w-7" src={ChevronsRight} alt="詳細へ進む" />
            </div>
        </div>
    </>
);

const UserQuestionRow = (props: {
    userQuestion: UserQuestion;
    answererId: string;
}) => {
    const { userQuestion, answererId } = props;
    const target = getStatusByDisplayInfo(userQuestion, answererId);

    if (!target) return null;
    return target.to !== undefined ? (
        <Link to={target.to} className="cursor-pointer">
            <UserQuestionRowContent
                displayDate={target.displayDate}
                title={userQuestion.title}
                label={target.label}
                styled={target.styled}
            />
        </Link>
    ) : (
        <UserQuestionRowContent
            displayDate={target.displayDate}
            title={userQuestion.title}
            label={target.label}
            styled={target.styled}
        />
    );
};

const isNotSent = (
    x: UserQuestion
): x is UserQuestion & { statusSendOrder: UserQuestionStatusSendOrderOneOf } =>
    x.statusSendOrder.status === "NOT_SENT";
const isSent = (
    x: UserQuestion
): x is UserQuestion & { statusSendOrder: UserQuestionStatusSendOrderOneOf1 } =>
    x.statusSendOrder.status === "SENT";
const isResponded = (
    x: UserQuestion
): x is UserQuestion & { statusSendOrder: UserQuestionStatusSendOrderOneOf2 } =>
    x.statusSendOrder.status === "RESPONDED";

export const QuestionsPage: FC = () => {
    const answererId = useParams().answererId as string;
    const { answerer, isLoading, isAgreed } =
        useRequireAgreedAnswerer(answererId);
    const navigate = useNavigate();
    const { userQuestions, isLoading: isGetUserQuestionLoading } =
        useUserQuestions(answererId);
    const notSentQuestion = useMemo(() => {
        const filtered = (userQuestions?.filter(isNotSent) || []).slice();

        filtered.sort(
            (a, b) =>
                new Date(
                    a.statusSendOrder.scheduledSendingDateTime ?? ""
                ).getTime() -
                new Date(
                    b.statusSendOrder.scheduledSendingDateTime ?? ""
                ).getTime()
        );

        return filtered;
    }, [userQuestions]);
    const sentQuestion = useMemo(() => {
        const filtered = (userQuestions?.filter(isSent) || []).slice();

        filtered.sort(
            (a, b) =>
                new Date(a.statusSendOrder.sentDateTime).getTime() -
                new Date(b.statusSendOrder.sentDateTime).getTime()
        );

        return filtered;
    }, [userQuestions]);
    const respondedQuestion = useMemo(() => {
        const filtered = (userQuestions?.filter(isResponded) || []).slice();

        filtered.sort(
            (a, b) =>
                new Date(a.statusSendOrder.respondedDateTime).getTime() -
                new Date(b.statusSendOrder.respondedDateTime).getTime()
        );

        return filtered;
    }, [userQuestions]);

    if (isLoading || isGetUserQuestionLoading) {
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <SpinAnimation />
            </div>
        );
    }

    if (!answerer || !isAgreed) {
        navigate(Path.notFound, { replace: true });
        return null;
    }

    return (
        <div>
            <div className="px-4 pt-2">
                <div className="mb-4 flex items-center gap-2">
                    <AnswererProfileImage
                        answererId={answererId}
                        src={answerer.profileImagePath}
                        imageSize="medium"
                    />
                    <p className="text-sm">{answerer.name}</p>
                </div>
                <Button
                    onClick={() =>
                        navigate(`${Path.addQuestion}/${answererId}`)
                    }
                    className="fixed bottom-4 right-4 h-12 px-4 py-2"
                >
                    <div className="flex">
                        <img
                            className="mr-2"
                            src={VectorIcon}
                            alt="吹き出しアイコン"
                        />
                        質問を追加する
                    </div>
                </Button>
            </div>
            {userQuestions?.length === 0 && (
                <p className="mx-auto mt-32 w-3/4 text-center text-xl">
                    まだ追加した質問はありません。質問を追加してみましょう！
                </p>
            )}
            {notSentQuestion.map((question) => {
                return (
                    <UserQuestionRow
                        key={question.id}
                        userQuestion={question}
                        answererId={answererId}
                    />
                );
            })}
            {sentQuestion.map((question) => {
                return (
                    <UserQuestionRow
                        key={question.id}
                        userQuestion={question}
                        answererId={answererId}
                    />
                );
            })}
            {respondedQuestion.map((question) => {
                return (
                    <UserQuestionRow
                        key={question.id}
                        userQuestion={question}
                        answererId={answererId}
                    />
                );
            })}
        </div>
    );
};
